import React, { useMemo, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Type,
  Underline,
} from 'lucide-react';
import { compressImageFile } from 'utils/imageUtils';
import {
  confirmDonation,
  createDonation,
  getDonationStatus,
} from 'services/donations';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';
import { FONT_FAMILY_OPTIONS } from './fontFamilyOptions';
import styles from './BodySection.module.scss';

interface BodySectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

interface BodyData {
  variant?: 'default' | 'donate';
  title?: string;
  subtitle?: string;
  body?: string;
  menuImageUrl?: string;
  menuImageHeight?: number;
  menuShowImageBanner?: boolean;
  menuImageBannerText?: string;
  titleBold?: boolean;
  titleItalic?: boolean;
  titleUnderline?: boolean;
  titleAlign?: 'left' | 'center' | 'right' | 'justify';
  titleFontSize?: number;
  titleColor?: string;
  titleFontFamily?: string;
  bodyBold?: boolean;
  bodyItalic?: boolean;
  bodyUnderline?: boolean;
  bodyAlign?: 'left' | 'center' | 'right' | 'justify';
  bodyFontSize?: number;
  bodyColor?: string;
  bodyFontFamily?: string;
  bodyBackgroundTransparent?: boolean;
  bodyBackgroundColor?: string;
  bodyBackgroundOverlay?: boolean;
  donateImageUrl?: string;
  donateSuggestedAmounts?: string;
  donateBankOptions?: string;
  donateCurrency?: string;
  donateMode?: 'one-time' | 'monthly';
  donateButtonLabel?: string;
  donateNote?: string;
  thankYouTitle?: string;
  thankYouSubtitle?: string;
  thankYouReceiptText?: string;
  thankYouShareFacebookLabel?: string;
  thankYouShareInstagramLabel?: string;
}

type IntlWithCurrencyHelpers = typeof Intl & {
  supportedValuesOf?: (key: 'currency') => string[];
  DisplayNames?: new (
    locales?: string | string[],
    options?: { type: 'currency' },
  ) => {
    of: (code: string) => string | undefined;
  };
};

const FALLBACK_CURRENCY_CODES = [
  'USD',
  'EUR',
  'GBP',
  'PHP',
  'JPY',
  'CAD',
  'AUD',
  'NZD',
  'CHF',
  'CNY',
  'HKD',
  'SGD',
  'INR',
  'KRW',
  'THB',
  'IDR',
  'MYR',
  'VND',
  'AED',
  'SAR',
  'QAR',
  'BHD',
  'KWD',
  'ZAR',
  'BRL',
  'MXN',
  'ARS',
  'CLP',
  'COP',
  'TRY',
  'RUB',
  'SEK',
  'NOK',
  'DKK',
  'PLN',
  'CZK',
  'HUF',
  'RON',
  'UAH',
  'ILS',
  'EGP',
  'NGN',
  'KES',
  'GHS',
];

function getAllCurrencyCodes(): string[] {
  const intlWithHelpers = Intl as IntlWithCurrencyHelpers;
  if (typeof intlWithHelpers.supportedValuesOf === 'function') {
    try {
      const values = intlWithHelpers.supportedValuesOf('currency');
      return values.filter((code) => /^[A-Z]{3}$/.test(code));
    } catch {
      return FALLBACK_CURRENCY_CODES;
    }
  }
  return FALLBACK_CURRENCY_CODES;
}

function getCurrencyDisplayLabel(code: string): string {
  const intlWithHelpers = Intl as IntlWithCurrencyHelpers;
  const DisplayNamesCtor = intlWithHelpers.DisplayNames;
  if (!DisplayNamesCtor) return code;
  try {
    const displayNames = new DisplayNamesCtor('en', { type: 'currency' });
    const label = displayNames.of(code);
    if (typeof label === 'string' && label.trim()) return `${code} - ${label}`;
  } catch {
    // ignore
  }
  return code;
}

const ALL_CURRENCY_OPTIONS = getAllCurrencyCodes().map((code) => ({
  code,
  label: getCurrencyDisplayLabel(code),
}));

function getData(data: Record<string, unknown>): BodyData {
  const variant = data.variant === 'donate' ? 'donate' : 'default';
  const defaultTitle =
    variant === 'donate' ? 'Support Our Ministry' : 'Page Content';
  const pageDefaultBodyText =
    'Write your page content here. This section is unique for each page.';
  const legacyDefaultBodyText =
    'Edit this text and tell your site visitors who you are. To edit, simply click directly on the text and add your own words. Use this text to go into more detail about your company. Make sure to include information about how your company came to be. A strong “About” page helps establish credibility and professionalism and lets the visitor know more about you.';
  const defaultBody =
    variant === 'donate'
      ? 'Your giving helps us continue outreach, discipleship, and community care.'
      : pageDefaultBodyText;
  const normalizeDefaultBody = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed === legacyDefaultBodyText) return pageDefaultBodyText;
    const escaped = pageDefaultBodyText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const repeats = trimmed.match(new RegExp(escaped, 'g'));
    if (repeats && repeats.length > 1) return pageDefaultBodyText;
    return value;
  };
  const rawTitle = typeof data.title === 'string' ? data.title.trim() : '';
  const rawBody = typeof data.body === 'string' ? data.body.trim() : '';
  const shouldUseDonateDefaultTitle =
    variant === 'donate' &&
    (!rawTitle || rawTitle.toLowerCase() === 'page content');
  const shouldUseDonateDefaultBody =
    variant === 'donate' &&
    (!rawBody ||
      rawBody === legacyDefaultBodyText ||
      rawBody.toLowerCase() === 'write your page content here.');

  return {
    variant,
    title: shouldUseDonateDefaultTitle
      ? defaultTitle
      : typeof data.title === 'string'
        ? data.title
        : defaultTitle,
    subtitle:
      typeof data.subtitle === 'string'
        ? data.subtitle
        : variant === 'donate'
          ? 'As a nonprofit, we rely on donations to provide life-changing help.'
          : pageDefaultBodyText,
    body: shouldUseDonateDefaultBody
      ? defaultBody
      : typeof data.body === 'string'
        ? variant === 'default'
          ? normalizeDefaultBody(data.body)
          : data.body
        : defaultBody,
    menuImageUrl:
      typeof data.menuImageUrl === 'string' && data.menuImageUrl.trim()
        ? data.menuImageUrl
        : heroPlaceholder,
    menuImageHeight:
      typeof data.menuImageHeight === 'number' &&
      Number.isFinite(data.menuImageHeight)
        ? Math.max(200, Math.min(680, data.menuImageHeight))
        : 320,
    menuShowImageBanner:
      typeof data.menuShowImageBanner === 'boolean'
        ? data.menuShowImageBanner
        : variant === 'default',
    menuImageBannerText:
      typeof data.menuImageBannerText === 'string'
        ? data.menuImageBannerText
        : 'Beliefs',
    titleBold: typeof data.titleBold === 'boolean' ? data.titleBold : false,
    titleItalic:
      typeof data.titleItalic === 'boolean' ? data.titleItalic : false,
    titleUnderline:
      typeof data.titleUnderline === 'boolean' ? data.titleUnderline : false,
    titleAlign:
      data.titleAlign === 'left' ||
      data.titleAlign === 'center' ||
      data.titleAlign === 'right' ||
      data.titleAlign === 'justify'
        ? data.titleAlign
        : 'left',
    titleFontSize:
      typeof data.titleFontSize === 'number' && data.titleFontSize > 0
        ? data.titleFontSize
        : 38,
    titleColor: typeof data.titleColor === 'string' ? data.titleColor : '',
    titleFontFamily:
      typeof data.titleFontFamily === 'string' ? data.titleFontFamily : '',
    bodyBold: typeof data.bodyBold === 'boolean' ? data.bodyBold : false,
    bodyItalic: typeof data.bodyItalic === 'boolean' ? data.bodyItalic : false,
    bodyUnderline:
      typeof data.bodyUnderline === 'boolean' ? data.bodyUnderline : false,
    bodyAlign:
      data.bodyAlign === 'left' ||
      data.bodyAlign === 'center' ||
      data.bodyAlign === 'right' ||
      data.bodyAlign === 'justify'
        ? data.bodyAlign
        : 'left',
    bodyFontSize:
      typeof data.bodyFontSize === 'number' && data.bodyFontSize > 0
        ? data.bodyFontSize
        : 16,
    bodyColor: typeof data.bodyColor === 'string' ? data.bodyColor : '',
    bodyFontFamily:
      typeof data.bodyFontFamily === 'string' ? data.bodyFontFamily : '',
    bodyBackgroundTransparent:
      typeof data.bodyBackgroundTransparent === 'boolean'
        ? data.bodyBackgroundTransparent
        : false,
    bodyBackgroundColor:
      typeof data.bodyBackgroundColor === 'string'
        ? data.bodyBackgroundColor
        : '#ffffff',
    bodyBackgroundOverlay:
      typeof data.bodyBackgroundOverlay === 'boolean'
        ? data.bodyBackgroundOverlay
        : false,
    donateImageUrl:
      typeof data.donateImageUrl === 'string' ? data.donateImageUrl : '',
    donateSuggestedAmounts:
      typeof data.donateSuggestedAmounts === 'string'
        ? data.donateSuggestedAmounts
        : '100,250,500,1000',
    donateBankOptions:
      typeof data.donateBankOptions === 'string'
        ? data.donateBankOptions
        : 'GCash,BDO,UnionBank',
    donateCurrency:
      typeof data.donateCurrency === 'string' && data.donateCurrency.trim()
        ? data.donateCurrency.trim().toUpperCase()
        : 'PHP',
    donateMode:
      data.donateMode === 'monthly' || data.donateMode === 'one-time'
        ? data.donateMode
        : 'one-time',
    donateButtonLabel:
      typeof data.donateButtonLabel === 'string'
        ? data.donateButtonLabel
        : 'Give Now',
    donateNote:
      typeof data.donateNote === 'string'
        ? data.donateNote
        : 'Secure online giving. Thank you for your generosity.',
    thankYouTitle:
      typeof data.thankYouTitle === 'string'
        ? data.thankYouTitle
        : 'Thank you for your gift!',
    thankYouSubtitle:
      typeof data.thankYouSubtitle === 'string'
        ? data.thankYouSubtitle
        : 'Your donation will help local wildlife thrive.',
    thankYouReceiptText:
      typeof data.thankYouReceiptText === 'string'
        ? data.thankYouReceiptText
        : "We've sent your donation receipt to [donor email address]",
    thankYouShareFacebookLabel:
      typeof data.thankYouShareFacebookLabel === 'string'
        ? data.thankYouShareFacebookLabel
        : 'Share on Facebook',
    thankYouShareInstagramLabel:
      typeof data.thankYouShareInstagramLabel === 'string'
        ? data.thankYouShareInstagramLabel
        : 'Share on Instagram',
  };
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.trim().replace(/^#/, '');
  if (raw.length === 3) {
    const r = Number.parseInt(raw[0] + raw[0], 16);
    const g = Number.parseInt(raw[1] + raw[1], 16);
    const b = Number.parseInt(raw[2] + raw[2], 16);
    if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b))
      return null;
    return { r, g, b };
  }
  if (raw.length !== 6) return null;
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b))
    return null;
  return { r, g, b };
}

export function BodySectionEditor({ data, onUpdateData }: BodySectionProps) {
  const d = getData(data);
  const isDonateVariant = d.variant === 'donate';
  const [activeField, setActiveField] = useState<'title' | 'body' | null>(
    'title',
  );
  const colorRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const defaultImageInputRef = useRef<HTMLInputElement>(null);

  const update = (key: keyof BodyData, value: unknown) => {
    onUpdateData({ ...data, [key]: value });
  };

  const activePrefix = activeField === 'title' ? 'title' : 'body';
  const toggleStyle = (key: 'Bold' | 'Italic' | 'Underline') => {
    if (!activeField) return;
    const styleKey = `${activePrefix}${key}` as keyof BodyData;
    const current = Boolean(d[styleKey] as boolean | undefined);
    update(styleKey, !current);
  };

  const updateAlign = (value: 'left' | 'center' | 'right' | 'justify') => {
    if (!activeField) return;
    update(`${activePrefix}Align` as keyof BodyData, value);
  };

  const updateFontSize = (delta: number) => {
    if (!activeField) return;
    const key = `${activePrefix}FontSize` as keyof BodyData;
    const current =
      typeof d[key] === 'number' ? d[key] : activeField === 'title' ? 38 : 16;
    update(key, Math.max(10, Math.min(72, current + delta)));
  };

  const updateColor = (value: string) => {
    if (!activeField) return;
    update(`${activePrefix}Color` as keyof BodyData, value);
  };

  const updateFontFamily = (value: string) => {
    if (!activeField) return;
    update(`${activePrefix}FontFamily` as keyof BodyData, value);
  };

  const setBackgroundTransparent = (value: boolean) => {
    update('bodyBackgroundTransparent', value);
  };

  const setBackgroundOverlay = (value: boolean) => {
    update('bodyBackgroundOverlay', value);
  };

  const updateBackgroundColor = (value: string) => {
    update('bodyBackgroundColor', value);
  };

  const shouldShowBackgroundColor =
    !d.bodyBackgroundTransparent || d.bodyBackgroundOverlay;

  return (
    <div className={styles.editorWrap}>
      {isDonateVariant ? (
        <div className={styles.elementRow}>
          <button
            type="button"
            className={`${styles.elementBtn} ${
              activeField === 'title' ? styles.elementBtnActive : ''
            }`}
            onClick={() => {
              if (!d.title?.trim()) update('title', 'Title');
              setActiveField('title');
            }}
          >
            <Type size={13} />
            Title
          </button>
          <button
            type="button"
            className={`${styles.elementBtn} ${
              activeField === 'body' ? styles.elementBtnActive : ''
            }`}
            onClick={() => {
              if (!d.body?.trim()) {
                update(
                  'body',
                  'Your giving helps us continue outreach, discipleship, and community care.',
                );
              }
              setActiveField('body');
            }}
          >
            <Type size={13} />
            Text
          </button>
        </div>
      ) : (
        <div className={styles.menuEditorCard}>
          <div className={styles.elementRow}>
            <button
              type="button"
              className={`${styles.elementBtn} ${
                activeField === 'title' ? styles.elementBtnActive : ''
              }`}
              onClick={() => setActiveField('title')}
            >
              <Type size={13} />
              Title
            </button>
            <button
              type="button"
              className={`${styles.elementBtn} ${
                activeField === 'body' ? styles.elementBtnActive : ''
              }`}
              onClick={() => setActiveField('body')}
            >
              <Type size={13} />
              Subtitle
            </button>
          </div>
          {activeField ? (
            <div className={styles.toolbar}>
              <select
                className={styles.toolSelect}
                value={
                  (activeField === 'title'
                    ? d.titleFontFamily
                    : d.bodyFontFamily) ?? ''
                }
                onChange={(e) => updateFontFamily(e.target.value)}
              >
                {FONT_FAMILY_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`${styles.toolBtn} ${
                  (activeField === 'title' ? d.titleBold : d.bodyBold)
                    ? styles.toolBtnActive
                    : ''
                }`}
                onClick={() => toggleStyle('Bold')}
              >
                <Bold size={12} />
              </button>
              <button
                type="button"
                className={`${styles.toolBtn} ${
                  (activeField === 'title' ? d.titleItalic : d.bodyItalic)
                    ? styles.toolBtnActive
                    : ''
                }`}
                onClick={() => toggleStyle('Italic')}
              >
                <Italic size={12} />
              </button>
              <button
                type="button"
                className={`${styles.toolBtn} ${
                  (activeField === 'title' ? d.titleUnderline : d.bodyUnderline)
                    ? styles.toolBtnActive
                    : ''
                }`}
                onClick={() => toggleStyle('Underline')}
              >
                <Underline size={12} />
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => updateFontSize(2)}
              >
                +
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => updateFontSize(-2)}
              >
                -
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => colorRef.current?.click()}
              >
                A
              </button>
              <input
                ref={colorRef}
                type="color"
                className={styles.colorInput}
                value={
                  (activeField === 'title' ? d.titleColor : d.bodyColor) ||
                  '#000000'
                }
                onChange={(e) => updateColor(e.target.value)}
              />
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => updateAlign('left')}
              >
                <AlignLeft size={12} />
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => updateAlign('center')}
              >
                <AlignCenter size={12} />
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => updateAlign('right')}
              >
                <AlignRight size={12} />
              </button>
              <button
                type="button"
                className={styles.toolBtn}
                onClick={() => updateAlign('justify')}
              >
                <AlignJustify size={12} />
              </button>
              <span className={styles.sizeBadge}>
                {activeField === 'title' ? d.titleFontSize : d.bodyFontSize}px
              </span>
            </div>
          ) : null}
          <label className={styles.fontLabel}>
            Title
            <input
              type="text"
              className={styles.titleInput}
              value={d.title ?? ''}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Page Content"
              onFocus={() => setActiveField('title')}
            />
          </label>
          <label className={styles.fontLabel}>
            Subtitle
            <textarea
              className={styles.bodyInput}
              value={d.subtitle ?? ''}
              onChange={(e) => update('subtitle', e.target.value)}
              placeholder="Write your page content here. This section is unique for each page."
              rows={3}
              onFocus={() => setActiveField('body')}
            />
          </label>
          <label className={styles.fontLabel}>
            Image banner URL
            <input
              type="text"
              className={styles.titleInput}
              value={d.menuImageUrl ?? ''}
              onChange={(e) => update('menuImageUrl', e.target.value)}
              placeholder="https://..."
            />
          </label>
          <label className={styles.fontLabel}>
            Image banner text
            <input
              type="text"
              className={styles.titleInput}
              value={d.menuImageBannerText ?? ''}
              onChange={(e) => update('menuImageBannerText', e.target.value)}
              placeholder="Beliefs"
            />
          </label>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={d.menuShowImageBanner ?? true}
              onChange={(e) => update('menuShowImageBanner', e.target.checked)}
            />
            Show image banner in this section
          </label>
          {d.menuShowImageBanner !== false ? (
            <>
              <div className={styles.elementRow}>
                <button
                  type="button"
                  className={styles.elementBtn}
                  onClick={() => defaultImageInputRef.current?.click()}
                >
                  Upload image
                </button>
                <button
                  type="button"
                  className={styles.elementBtn}
                  onClick={() => update('menuImageUrl', '')}
                >
                  Clear image
                </button>
              </div>
              <input
                ref={defaultImageInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  compressImageFile(file)
                    .then((url) => update('menuImageUrl', url))
                    .catch(() => {});
                  e.target.value = '';
                }}
              />
              <label className={styles.fontLabel}>
                Image banner height (px)
                <input
                  type="number"
                  min={200}
                  max={680}
                  step={10}
                  className={styles.titleInput}
                  value={d.menuImageHeight ?? 320}
                  onChange={(e) =>
                    update(
                      'menuImageHeight',
                      Number.parseInt(e.target.value || '320', 10),
                    )
                  }
                />
              </label>
            </>
          ) : null}
        </div>
      )}

      {isDonateVariant && activeField ? (
        <div className={styles.toolbar}>
          <select
            className={styles.toolSelect}
            value={
              (activeField === 'title'
                ? d.titleFontFamily
                : d.bodyFontFamily) ?? ''
            }
            onChange={(e) => updateFontFamily(e.target.value)}
          >
            {FONT_FAMILY_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`${styles.toolBtn} ${
              (activeField === 'title' ? d.titleBold : d.bodyBold)
                ? styles.toolBtnActive
                : ''
            }`}
            onClick={() => toggleStyle('Bold')}
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolBtn} ${
              (activeField === 'title' ? d.titleItalic : d.bodyItalic)
                ? styles.toolBtnActive
                : ''
            }`}
            onClick={() => toggleStyle('Italic')}
          >
            <Italic size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolBtn} ${
              (activeField === 'title' ? d.titleUnderline : d.bodyUnderline)
                ? styles.toolBtnActive
                : ''
            }`}
            onClick={() => toggleStyle('Underline')}
          >
            <Underline size={12} />
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => updateFontSize(2)}
          >
            +
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => updateFontSize(-2)}
          >
            -
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => colorRef.current?.click()}
          >
            A
          </button>
          <input
            ref={colorRef}
            type="color"
            className={styles.colorInput}
            value={
              (activeField === 'title' ? d.titleColor : d.bodyColor) ||
              '#000000'
            }
            onChange={(e) => updateColor(e.target.value)}
          />
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => updateAlign('left')}
          >
            <AlignLeft size={12} />
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => updateAlign('center')}
          >
            <AlignCenter size={12} />
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => updateAlign('right')}
          >
            <AlignRight size={12} />
          </button>
          <button
            type="button"
            className={styles.toolBtn}
            onClick={() => updateAlign('justify')}
          >
            <AlignJustify size={12} />
          </button>
          <span className={styles.sizeBadge}>
            {activeField === 'title' ? d.titleFontSize : d.bodyFontSize}px
          </span>
        </div>
      ) : null}

      {isDonateVariant ? (
        <div className={styles.donateEditorCard}>
          <label className={styles.fontLabel}>
            Title
            <input
              type="text"
              className={styles.titleInput}
              value={d.title ?? ''}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Support Our Ministry"
            />
          </label>
          <label className={styles.fontLabel}>
            Subtitle
            <textarea
              className={styles.bodyInput}
              value={d.subtitle ?? ''}
              onChange={(e) => update('subtitle', e.target.value)}
              placeholder="Subtitle for donation page"
              rows={2}
            />
          </label>
          <label className={styles.fontLabel}>
            Right frame image URL
            <input
              type="text"
              className={styles.titleInput}
              value={d.donateImageUrl ?? ''}
              onChange={(e) => update('donateImageUrl', e.target.value)}
              placeholder="https://..."
            />
          </label>
          <div className={styles.elementRow}>
            <button
              type="button"
              className={styles.elementBtn}
              onClick={() => imageInputRef.current?.click()}
            >
              Upload image
            </button>
            <button
              type="button"
              className={styles.elementBtn}
              onClick={() => update('donateImageUrl', '')}
            >
              Clear image
            </button>
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              compressImageFile(file)
                .then((url) => update('donateImageUrl', url))
                .catch(() => {});
              e.target.value = '';
            }}
          />
          <label className={styles.fontLabel}>
            Suggested amounts (comma-separated)
            <input
              type="text"
              className={styles.titleInput}
              value={d.donateSuggestedAmounts ?? ''}
              onChange={(e) => update('donateSuggestedAmounts', e.target.value)}
              placeholder="100,250,500,1000"
            />
          </label>
          <label className={styles.fontLabel}>
            Bank options (comma-separated)
            <input
              type="text"
              className={styles.titleInput}
              value={d.donateBankOptions ?? ''}
              onChange={(e) => update('donateBankOptions', e.target.value)}
              placeholder="GCash,BDO,UnionBank"
            />
          </label>
          <label className={styles.fontLabel}>
            Default currency
            <select
              className={styles.titleInput}
              value={d.donateCurrency ?? 'PHP'}
              onChange={(e) => update('donateCurrency', e.target.value)}
            >
              {ALL_CURRENCY_OPTIONS.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Default donation mode
            <select
              className={styles.titleInput}
              value={d.donateMode ?? 'one-time'}
              onChange={(e) =>
                update('donateMode', e.target.value as 'one-time' | 'monthly')
              }
            >
              <option value="one-time">One-time</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          <label className={styles.fontLabel}>
            Donate button label
            <input
              type="text"
              className={styles.titleInput}
              value={d.donateButtonLabel ?? ''}
              onChange={(e) => update('donateButtonLabel', e.target.value)}
              placeholder="Give Now"
            />
          </label>
          <label className={styles.fontLabel}>
            Donation note
            <textarea
              className={styles.bodyInput}
              value={d.donateNote ?? ''}
              onChange={(e) => update('donateNote', e.target.value)}
              placeholder="Secure online giving. Thank you for your generosity."
              rows={3}
            />
          </label>
          <label className={styles.fontLabel}>
            Thank-you title
            <input
              type="text"
              className={styles.titleInput}
              value={d.thankYouTitle ?? ''}
              onChange={(e) => update('thankYouTitle', e.target.value)}
            />
          </label>
          <label className={styles.fontLabel}>
            Thank-you subtitle
            <input
              type="text"
              className={styles.titleInput}
              value={d.thankYouSubtitle ?? ''}
              onChange={(e) => update('thankYouSubtitle', e.target.value)}
            />
          </label>
          <label className={styles.fontLabel}>
            Receipt message
            <textarea
              className={styles.bodyInput}
              rows={2}
              value={d.thankYouReceiptText ?? ''}
              onChange={(e) => update('thankYouReceiptText', e.target.value)}
            />
          </label>
          <div className={styles.elementRow}>
            <label className={styles.fontLabel} style={{ flex: 1 }}>
              Facebook button text
              <input
                type="text"
                className={styles.titleInput}
                value={d.thankYouShareFacebookLabel ?? ''}
                onChange={(e) =>
                  update('thankYouShareFacebookLabel', e.target.value)
                }
              />
            </label>
            <label className={styles.fontLabel} style={{ flex: 1 }}>
              Instagram button text
              <input
                type="text"
                className={styles.titleInput}
                value={d.thankYouShareInstagramLabel ?? ''}
                onChange={(e) =>
                  update('thankYouShareInstagramLabel', e.target.value)
                }
              />
            </label>
          </div>
        </div>
      ) : null}

      {isDonateVariant ? (
        <div className={styles.backgroundControls}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={d.bodyBackgroundTransparent}
              onChange={(e) => setBackgroundTransparent(e.target.checked)}
            />
            Transparent background (show page background)
          </label>

          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={d.bodyBackgroundOverlay}
              onChange={(e) => setBackgroundOverlay(e.target.checked)}
            />
            Overlay (semi-transparent)
          </label>

          {shouldShowBackgroundColor ? (
            <div className={styles.bgColorRow}>
              <span className={styles.bgColorLabel}>Background color</span>
              <input
                type="color"
                value={d.bodyBackgroundColor || '#ffffff'}
                onChange={(e) => updateBackgroundColor(e.target.value)}
                className={styles.bgColorInput}
                aria-label="Body background color"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {isDonateVariant ? (
        <>
          <input
            className={styles.titleInput}
            value={(data.title as string) || ''}
            onChange={(e) => update('title', e.target.value)}
            placeholder={d.title || 'Page Content'}
            onFocus={() => setActiveField('title')}
          />
          <textarea
            className={styles.bodyInput}
            value={(data.body as string) || ''}
            onChange={(e) => update('body', e.target.value)}
            placeholder={d.body || 'Write your page content here.'}
            onFocus={() => setActiveField('body')}
          />
        </>
      ) : null}
    </div>
  );
}

function BodySection({ data }: Omit<BodySectionProps, 'onUpdateData'>) {
  const d = getData(data);
  const isDonateVariant = d.variant === 'donate';
  const alpha = 0.78;
  const rgb = hexToRgb(d.bodyBackgroundColor || '#ffffff');
  const overlayBackground = rgb
    ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
    : d.bodyBackgroundColor;

  const sectionBackground =
    d.bodyBackgroundTransparent && !d.bodyBackgroundOverlay
      ? 'transparent'
      : d.bodyBackgroundOverlay
        ? overlayBackground
        : d.bodyBackgroundColor;

  const sectionStyle: React.CSSProperties = { background: sectionBackground };
  const titleStyle: React.CSSProperties = {
    fontWeight: d.titleBold ? 700 : 400,
    fontStyle: d.titleItalic ? 'italic' : 'normal',
    textDecoration: d.titleUnderline ? 'underline' : 'none',
    textAlign: d.titleAlign ?? 'left',
    fontSize: `${d.titleFontSize ?? 38}px`,
    ...(d.titleFontFamily ? { fontFamily: d.titleFontFamily } : {}),
    ...(d.titleColor ? { color: d.titleColor } : {}),
  };
  const bodyStyle: React.CSSProperties = {
    fontWeight: d.bodyBold ? 700 : 400,
    fontStyle: d.bodyItalic ? 'italic' : 'normal',
    textDecoration: d.bodyUnderline ? 'underline' : 'none',
    textAlign: d.bodyAlign ?? 'left',
    fontSize: `${d.bodyFontSize ?? 16}px`,
    ...(d.bodyFontFamily ? { fontFamily: d.bodyFontFamily } : {}),
    ...(d.bodyColor ? { color: d.bodyColor } : {}),
  };

  const suggestedAmounts = (d.donateSuggestedAmounts ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  const bankOptions = (d.donateBankOptions ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  const [selectedAmount, setSelectedAmount] = useState(
    suggestedAmounts[0] ?? '',
  );
  const [customAmount, setCustomAmount] = useState('');
  const [selectedBank, setSelectedBank] = useState(bankOptions[0] ?? '');
  const [selectedCurrency, setSelectedCurrency] = useState(
    d.donateCurrency ?? 'PHP',
  );
  const [donateMode, setDonateMode] = useState<'one-time' | 'monthly'>(
    d.donateMode ?? 'one-time',
  );
  const [donorEmail, setDonorEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const getCompanyDomainFromPath = (): string => {
    const match = window.location.pathname.match(/^\/www\/([^/]+)\/[^/]+$/i);
    if (match && typeof match[1] === 'string') {
      return match[1].trim().toLowerCase();
    }
    return '';
  };

  const isConfirmedStatus = (status: string | undefined): boolean => {
    const normalized = (status ?? '').trim().toLowerCase();
    return (
      normalized === 'paid' ||
      normalized === 'confirmed' ||
      normalized === 'success'
    );
  };

  const effectiveAmount = useMemo(() => {
    const custom = Number.parseFloat(customAmount);
    if (Number.isFinite(custom) && custom > 0) return custom;
    const selected = Number.parseFloat(selectedAmount);
    if (Number.isFinite(selected) && selected > 0) return selected;
    return 0;
  }, [customAmount, selectedAmount]);

  const formatAmountLabel = (value: string, currencyCode: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(0);
    }
    const numeric = Number.parseFloat(trimmed.replace(/,/g, ''));
    if (!Number.isFinite(numeric)) return trimmed;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  const handleDonate = async () => {
    if (!isDonateVariant) return;
    if (effectiveAmount <= 0) {
      setPaymentError('Please select or enter a valid donation amount.');
      return;
    }
    if (!selectedBank.trim()) {
      setPaymentError('Please select a bank.');
      return;
    }

    setIsSubmitting(true);
    setPaymentError(null);
    try {
      const createRes = await createDonation({
        pageId: 'page-default-donate',
        slug: 'donate',
        companyDomain: getCompanyDomainFromPath(),
        amount: effectiveAmount,
        currency: selectedCurrency,
        bank: selectedBank,
        mode: donateMode,
        ...(donorEmail.trim() ? { donorEmail: donorEmail.trim() } : {}),
      });

      const redirectUrl =
        createRes.deepLinkUrl || createRes.checkoutUrl || createRes.redirectUrl;
      if (redirectUrl && redirectUrl.trim()) {
        setIsRedirecting(true);
        window.location.assign(redirectUrl);
        return;
      }

      const confirmRes = await confirmDonation({
        donationId: createRes.donationId,
      });

      if (isConfirmedStatus(confirmRes.status)) {
        setShowThankYou(true);
        return;
      }

      const statusRes = await getDonationStatus(createRes.donationId);
      if (isConfirmedStatus(statusRes.status)) {
        setShowThankYou(true);
      } else {
        setPaymentError(
          'Donation submitted, but payment is not confirmed yet. Please try again.',
        );
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to process donation.';
      setPaymentError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className={`${styles.section} ${
        isDonateVariant ? styles.sectionDonate : styles.sectionDefault
      }`}
      style={sectionStyle}
    >
      <div
        className={`${styles.inner} ${
          isDonateVariant ? styles.innerDonate : styles.innerDefault
        }`}
      >
        {isDonateVariant ? (
          showThankYou ? (
            <div className={styles.thankYouWrap}>
              <h2 className={styles.thankYouTitle}>
                {d.thankYouTitle || 'Thank you for your gift!'}
              </h2>
              <p className={styles.thankYouSubtitle}>
                {d.thankYouSubtitle ||
                  'Your donation will help local wildlife thrive.'}
              </p>
              <p className={styles.thankYouReceipt}>
                {d.thankYouReceiptText ||
                  "We've sent your donation receipt to [donor email address]"}
              </p>
              <div className={styles.thankYouActions}>
                <a
                  href="https://www.facebook.com/sharer/sharer.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.thankYouShareBtn}
                >
                  {d.thankYouShareFacebookLabel || 'Share on Facebook'}
                </a>
                <a
                  href="https://www.instagram.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.thankYouShareBtn}
                >
                  {d.thankYouShareInstagramLabel || 'Share on Instagram'}
                </a>
              </div>
            </div>
          ) : (
            <div className={styles.donateWrap}>
              <div className={styles.donateHeroLayout}>
                <div className={styles.donateContentPane}>
                  <h2 className={styles.title} style={titleStyle}>
                    {d.title}
                  </h2>
                  {d.subtitle?.trim() ? (
                    <p className={styles.subtitle}>{d.subtitle}</p>
                  ) : null}
                  <p className={styles.body} style={bodyStyle}>
                    {d.body}
                  </p>
                  <p className={styles.donateImpact}>
                    ❤ 100% of your donation will be spent on children and
                    families who are counting on you.
                  </p>
                  <div className={styles.paymentSection}>
                    <h3 className={styles.paymentTitle}>🎁 Your Gift</h3>
                    <label className={styles.paymentLabel}>Amount*</label>
                    <div className={styles.donateAmounts}>
                      {suggestedAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          className={`${styles.amountChip} ${
                            selectedAmount === amount &&
                            customAmount.trim() === ''
                              ? styles.amountChipActive
                              : ''
                          }`}
                          onClick={() => {
                            setSelectedAmount(amount);
                            setCustomAmount('');
                          }}
                        >
                          {formatAmountLabel(amount, selectedCurrency)}
                        </button>
                      ))}
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${styles.paymentInput} ${styles.paymentInputAmount}`}
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder={formatAmountLabel('0', selectedCurrency)}
                      />
                    </div>

                    <label className={styles.paymentLabel}>Currency</label>
                    <select
                      className={styles.paymentInput}
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                    >
                      {ALL_CURRENCY_OPTIONS.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                    <label className={styles.paymentLabel}>Bank</label>
                    <select
                      className={styles.paymentInput}
                      value={selectedBank}
                      onChange={(e) => setSelectedBank(e.target.value)}
                    >
                      {bankOptions.map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>

                    <div className={styles.modeRow}>
                      <label>
                        <input
                          type="radio"
                          checked={donateMode === 'one-time'}
                          onChange={() => setDonateMode('one-time')}
                        />
                        One Time Gift
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={donateMode === 'monthly'}
                          onChange={() => setDonateMode('monthly')}
                        />
                        Monthly Gift
                      </label>
                    </div>

                    <input
                      type="email"
                      className={styles.paymentInput}
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="Donor email (optional)"
                    />

                    <button
                      type="button"
                      className={styles.donatePrimaryBtn}
                      onClick={() => {
                        void handleDonate();
                      }}
                      disabled={isSubmitting || isRedirecting}
                    >
                      {isRedirecting
                        ? 'Redirecting to bank...'
                        : isSubmitting
                          ? 'Processing...'
                          : d.donateButtonLabel || 'Give Now'}
                    </button>

                    {d.donateNote?.trim() ? (
                      <p className={styles.donateNote}>{d.donateNote}</p>
                    ) : null}
                    {paymentError ? (
                      <p className={styles.paymentError}>{paymentError}</p>
                    ) : null}
                  </div>
                </div>
                <div className={styles.donateImagePane}>
                  {d.donateImageUrl?.trim() ? (
                    <img
                      src={d.donateImageUrl}
                      alt=""
                      className={styles.donateImage}
                    />
                  ) : (
                    <div className={styles.donateImagePlaceholder}>
                      Upload image in editor
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ) : null}
        {!isDonateVariant ? (
          <div className={styles.menuPageLayout}>
            {d.menuShowImageBanner !== false ? (
              <div
                className={styles.menuHero}
                style={{ height: `${d.menuImageHeight ?? 320}px` }}
              >
                <img
                  src={d.menuImageUrl || heroPlaceholder}
                  alt=""
                  className={styles.menuHeroImage}
                />
                {d.menuImageBannerText?.trim() ? (
                  <div className={styles.menuHeroTextWrap}>
                    <h2 className={styles.menuHeroText}>
                      {d.menuImageBannerText}
                    </h2>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className={styles.menuContent}>
              <h2 className={styles.title} style={titleStyle}>
                {d.title}
              </h2>
              <p className={styles.body} style={bodyStyle}>
                {d.subtitle || d.body}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default BodySection;
